import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Loader2, Mic, RefreshCw, Trash2, Undo2 } from "lucide-react";
import { motion, AnimatePresence } from "motion/react";

import { useToast } from "../../components/ui/toast";
import { useHasPermission } from "../../context/AuthContext";
import { CloneVoiceModal } from "./clone-voice-modal";
import {
  createClone,
  deleteClone,
  listClones,
  recloneVoice,
  type CloneInput,
} from "./api";
import type { ClonedVoice } from "./types";
import { useAudioPlayer } from "./use-audio-player";
import { getSampleUrl } from "./api";
import { FluidBlobAvatar } from "./fluid-blob-avatar";

function hashString(str: string): number {
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    hash = (hash << 5) - hash + str.charCodeAt(i);
    hash |= 0;
  }
  return hash;
}

function getVoiceHue(index: number): number {
  // Using the Golden Angle (137.5 degrees) based on the list index guarantees that 
  // adjacent cards will always have maximum color contrast (exactly 137.5 degrees apart)
  return Math.floor(index * 137.5) % 360;
}

const VOICES_KEY = ["voices", "clones"];

function VoiceCard({ voice, index, onReclone, onDelete, canClone }: { 
  voice: ClonedVoice; 
  index: number;
  onReclone: () => void;
  onDelete: () => void;
  canClone: boolean;
}) {
  const [deleteCountdown, setDeleteCountdown] = useState<number | null>(null);

  const { playingId, progress, play, pause } = useAudioPlayer();
  const playing = playingId === voice.id;
  const hue = getVoiceHue(index);
  const [url, setUrl] = useState<string | null>(null);

  useEffect(() => {
    getSampleUrl(voice.id).then(setUrl).catch(console.error);
  }, [voice.id]);

  useEffect(() => {
    if (deleteCountdown === null) return;
    if (deleteCountdown <= 0) {
      onDelete();
      return;
    }
    const timer = setTimeout(() => setDeleteCountdown(deleteCountdown - 1), 1000);
    return () => clearTimeout(timer);
  }, [deleteCountdown, onDelete]);

  const togglePlay = (e?: React.MouseEvent) => {
    if (e) e.stopPropagation();
    if (playing) pause();
    else if (url) play(voice.id, url);
  };

  const isDeleting = deleteCountdown !== null;

  return (
    <motion.div 
      layout
      initial={{ opacity: 0, scale: 0.95 }}
      animate={{ opacity: 1, scale: 1 }}
      exit={{ opacity: 0, scale: 0.9 }}
      transition={{ duration: 0.2 }}
      className="flex flex-col items-center gap-3 rounded-xl shadow-card dark:shadow-card-dark bg-paper-card dark:bg-[#292524] p-4 group transition-all hover:shadow-card-hover relative overflow-hidden"
    >
      <div className="absolute top-3 right-3 flex items-center gap-1 opacity-0 focus-within:opacity-100 group-hover:opacity-100 transition-opacity">
        {canClone && deleteCountdown === null && (
          <>
            <button
              type="button"
              onClick={onReclone}
              className="p-1.5 text-ink-3 hover:text-ink hover:bg-line/50 rounded-lg transition-colors outline-none focus:ring-2 focus:ring-brand"
              aria-label="Re-record voice"
            >
              <RefreshCw className="h-4 w-4" />
            </button>
            <button
              type="button"
              onClick={() => setDeleteCountdown(5)}
              className="p-1.5 text-ink-3 hover:text-danger hover:bg-danger-soft rounded-lg transition-colors outline-none focus:ring-2 focus:ring-danger"
              aria-label="Delete voice"
            >
              <Trash2 className="h-4 w-4" />
            </button>
          </>
        )}
      </div>

      <motion.div 
        animate={isDeleting ? { rotate: 120, scale: 0, opacity: 0 } : { rotate: 0, scale: 1, opacity: 1 }} 
        transition={{ duration: 0.4, ease: "anticipate" }}
        className="shrink-0 mt-2"
      >
        <FluidBlobAvatar 
          hue={hue} 
          playing={playing} 
          onToggle={togglePlay} 
          className="w-20 h-20"
        />
      </motion.div>
      
      <div className="flex flex-col items-center text-center">
        <p className="truncate text-lg font-semibold text-ink leading-tight">{voice.local_voice_name}</p>
        <div className="mt-2.5 flex items-center justify-center gap-1.5 flex-wrap">
          <span className="bg-line/50 px-2 py-0.5 rounded text-[10px] font-semibold uppercase tracking-wider">{voice.gender}</span>
          <span className="bg-line/50 px-2 py-0.5 rounded text-[10px] font-semibold uppercase tracking-wider">{voice.language}</span>
        </div>
        <p className="mt-3 text-[11px] font-medium text-ink-3 opacity-90">
           Added {new Date(voice.created_at).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric'})}
        </p>
      </div>

      {canClone && deleteCountdown !== null && (
        <div className="absolute inset-0 bg-canvas/80 backdrop-blur-sm flex flex-col items-center justify-center gap-3">
          <span className="text-sm text-danger font-medium flex items-center gap-1.5">
            <Loader2 className="h-4 w-4 animate-spin" />
            Deleting in {deleteCountdown}s
          </span>
          <button
            type="button"
            onClick={() => setDeleteCountdown(null)}
            className="text-sm font-medium bg-white text-ink border border-line hover:bg-line/20 px-4 py-1.5 rounded-full flex items-center gap-1.5 outline-none focus:ring-2 focus:ring-ink"
          >
            <Undo2 className="h-3.5 w-3.5" />
            Undo
          </button>
        </div>
      )}
    </motion.div>
  );
}

export function VoicesPage() {
  const toast = useToast();
  const queryClient = useQueryClient();
  const canClone = useHasPermission("voice.clone");

  const [cloning, setCloning] = useState(false);
  const [recloning, setRecloning] = useState<ClonedVoice | null>(null);

  const voices = useQuery({ queryKey: VOICES_KEY, queryFn: listClones });

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: VOICES_KEY });
    queryClient.invalidateQueries({ queryKey: ["voice", "options"] });
  };

  const create = useMutation({
    mutationFn: (input: CloneInput) => createClone(input),
    onSuccess: (voice) => {
      invalidate();
      toast.success(`${voice.local_voice_name} is ready`);
    },
  });

  const reclone = useMutation({
    mutationFn: ({ id, clip }: { id: string; clip: Blob }) => recloneVoice(id, clip),
    onSuccess: (voice) => {
      invalidate();
      toast.success(`${voice.local_voice_name} updated`);
    },
  });

  const remove = useMutation({
    mutationFn: (id: string) => deleteClone(id),
    onSuccess: () => {
      invalidate();
      toast.success("Voice deleted");
    },
    onError: (error) => toast.danger("Could not delete the voice", (error as Error)?.message),
  });

  const rows = voices.data ?? [];

  return (
    <div className="space-y-4 px-4 sm:px-5 md:px-6 pb-6 pt-3 sm:pt-4">
      <header className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-sm font-semibold text-ink">Voices</h1>

        </div>
        {canClone && (
          <button
            type="button"
            onClick={() => setCloning(true)}
            className="inline-flex items-center gap-2 rounded-lg bg-accent-500 px-4 py-2 text-sm font-semibold text-ink hover:bg-accent-600 transition-colors"
          >
            <Mic className="h-4 w-4" />
            Clone a voice
          </button>
        )}
      </header>

      {voices.isLoading && (
        <div className="flex h-40 items-center justify-center border border-dashed border-line rounded-xl bg-canvas/50">
          <p className="flex items-center gap-2 text-sm text-ink-2">
            <Loader2 className="h-4 w-4 animate-spin" /> Loading voices…
          </p>
        </div>
      )}

      {voices.isError && (
        <div className="flex h-40 items-center justify-center border border-dashed border-danger-soft bg-danger-soft/20 rounded-xl">
          <p className="text-sm text-danger font-medium">
            Could not load voices. {(voices.error as Error)?.message}
          </p>
        </div>
      )}

      {!voices.isLoading && !voices.isError && rows.length === 0 && (
        <div className="rounded-xl border border-dashed border-line p-16 text-center bg-canvas/30">
          <div className="w-12 h-12 bg-soft rounded-full flex items-center justify-center mx-auto mb-4">
             <Mic className="h-5 w-5 text-ink-3" />
          </div>
          <p className="text-base font-semibold text-ink">No cloned voices yet</p>
          <p className="mt-2 text-sm text-ink-2 max-w-sm mx-auto">
            {canClone
              ? "Record ten seconds of speech to create a custom voice your agents can use immediately."
              : "Ask your workspace owner for permission to clone a voice."}
          </p>
          {canClone && (
             <button
               type="button"
               onClick={() => setCloning(true)}
               className="mt-6 inline-flex items-center gap-2 rounded-full bg-white border border-line px-5 py-2 text-sm font-medium text-ink hover:bg-soft shadow-sm transition-all"
             >
               Get Started
             </button>
          )}
        </div>
      )}

      {rows.length > 0 && (
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
          <AnimatePresence mode="popLayout">
            {rows.map((voice, idx) => (
              <VoiceCard
                key={voice.id}
                voice={voice}
                index={idx}
                canClone={canClone}
                onReclone={() => setRecloning(voice)}
                onDelete={() => remove.mutate(voice.id)}
              />
            ))}
          </AnimatePresence>
        </div>
      )}

      {cloning && (
        <CloneVoiceModal
          onClose={() => setCloning(false)}
          onSubmit={(input) => create.mutateAsync(input).then(() => undefined)}
        />
      )}

      {recloning && (
        <CloneVoiceModal
          existing={recloning}
          onClose={() => setRecloning(null)}
          onSubmit={({ clip }) =>
            reclone.mutateAsync({ id: recloning.id, clip }).then(() => undefined)
          }
        />
      )}
    </div>
  );
}
